// ./src/components/generic/ExpenseTypeSelector/ExpenseTypeSelector.tsx
//
// Expense type selector using Chip buttons.
// Always renders chips (no Select dropdown) since there are only a few options.

import React, { useMemo, useEffect, useCallback } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  getExpenseTypeOptionsByMode,
  EXPENSE_TYPE_MAP,
  type ExpenseTypeMode,
} from "@/config/expenseTypes";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ExpenseTypeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected expense type value */
  value?: number | null | undefined;

  /** Callback fired when expense type changes: (name, value) */
  onChange?: (name: string, value: number | null) => void;

  /** Whether the field is required (auto-selects first option if null) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the input wrapper */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Which expense types to show: "all" or "core" (refuel/expense/checkpoint only) */
  mode?: ExpenseTypeMode;

  /** Chip variant */
  chipVariant?: "outline" | "filled" | "light";
}

// ============================================
// Chip Styles (matching TravelTypeSelector)
// ============================================

const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "20px",
  minWidth: "20px",
  height: "20px",
  paddingRight: "4px",
  overflow: "visible",
};

const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const ExpenseTypeSelectorComponent: React.FC<ExpenseTypeSelectorProps> = ({
  name = "expenseType",
  value,
  onChange,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
  mode = "all",
  chipVariant = "outline",
}) => {
  const { t } = useTranslation("common");

  // Build options list with translated labels, filtered by mode
  const options = useMemo(
    () =>
      getExpenseTypeOptionsByMode(mode).map((preset) => ({
        value: "" + preset.value,
        label: t(preset.labelKey),
      })),
    [t, mode]
  );

  // Auto-select first option if required and no value
  useEffect(() => {
    if (required && !value && options.length > 0) {
      onChange?.(name, Number(options[0].value));
    }
  }, [required, value, options, name, onChange]);

  // Handle chip click
  const handleClick = useCallback(
    (chipValue: string) => {
      const numValue = Number(chipValue);
      if (numValue === value) {
        // Clicking the already-selected chip — deselect if not required
        if (!required) {
          onChange?.(name, null);
        }
      } else {
        onChange?.(name, numValue);
      }
    },
    [name, value, required, onChange]
  );

  // Build chip elements
  const chips = useMemo(
    () =>
      options.map((option) => {
        const expenseType = EXPENSE_TYPE_MAP[option.value];
        const iconName = expenseType?.iconName ?? "Receipt";

        return (
          <Chip
            key={option.value}
            checked={value != null && "" + value === option.value}
            onChange={() => handleClick(option.value)}
            variant={chipVariant}
            size="md"
            disabled={disabled}
            icon={<AppIcon iconName={iconName} size={20} />}
            styles={{
              label: CHIP_LABEL_STYLES,
              iconWrapper: CHIP_ICON_WRAPPER_STYLES,
              root: CHIP_ROOT_STYLES,
            }}
          >
            {option.label}
          </Chip>
        );
      }),
    [options, chipVariant, disabled, value, handleClick]
  );

  return (
    <Input.Wrapper label={label} required={required} error={error} size={size}>
      <Box mt={4} style={GROUP_STYLES}>
        {chips}
      </Box>
    </Input.Wrapper>
  );
};

export const ExpenseTypeSelector = React.memo(ExpenseTypeSelectorComponent);